// Retention prune (szzt-spec.md §16).
//
// Prune is a write-side PUBLISHER action, the opposite trust class from the gateway's delist
// (§17.4 "delist, not erase"): the publisher deletes historical versions from their OWN PDS.
// §16: "Pruning deletes a `app.szzt.deployment` record and its `ing.dasl.masl` manifest record;
// a resource blob is releasable once no retained manifest references it. The active version
// (`site.current`) MUST NOT be pruned."
//
// There is no repo write that deletes a blob directly: an atproto PDS garbage-collects a blob once
// no record references it. So the prune WRITE is a single atomic `applyWrites` of delete ops for the
// targeted deployment + manifest records; the releasable blobs fall out of PDS GC afterward. The
// planner reports which blob CIDs that will be (referenced by no RETAINED manifest) so the caller can
// verify / report.
//
// Split like the rest of the write path: a PURE planner over the supplied record graph
// (`planPrune`), and a THIN executor over the `PdsClient` seam (`prune`). Enumerating a repo's
// deployments (com.atproto.repo.listRecords) is the caller's job — the same seam boundary the other
// packages keep; the planner takes the graph as data so it stays deterministic + unit-testable.

import type { MaslDocument } from "@szzt/core";
import { parseAtUri, readStrongRef } from "@szzt/records";
import { COLLECTION } from "./records.ts";
import {
  type ApplyWrite,
  type CommitRef,
  type PdsClient,
  SwapFailedError,
} from "./types.ts";

/**
 * A single deployment as the planner needs to see it: its record URI, its manifest record URI, and
 * the resource blob CIDs that manifest references. Assemble one with {@link deploymentView} from a
 * resolved deployment + manifest, or by hand.
 */
export interface DeploymentView {
  /** AT-URI of the `app.szzt.deployment` record. */
  deploymentUri: string;
  /** AT-URI of the paired `ing.dasl.masl` manifest record (the deployment's `tile.uri`). */
  manifestUri: string;
  /** Deduped resource blob CIDs referenced by this deployment's manifest. */
  blobCids: string[];
}

/**
 * Extract the deduped set of resource blob CIDs a manifest references (§7.2): every
 * `resources[*].src.ref.$link`. The `/` alias duplicates the entry document's blob CID, so
 * dedup is required and load-bearing — a blob is only releasable when NO retained manifest lists it.
 */
export function manifestBlobCids(manifest: MaslDocument): string[] {
  const cids = new Set<string>();
  for (const entry of Object.values(manifest.resources)) {
    cids.add(entry.src.ref.$link);
  }
  return [...cids];
}

/** Assemble a {@link DeploymentView} from a deployment/manifest URI pair and the inline manifest. */
export function deploymentView(
  deploymentUri: string,
  manifestUri: string,
  manifest: MaslDocument,
): DeploymentView {
  return { deploymentUri, manifestUri, blobCids: manifestBlobCids(manifest) };
}

/** Input to the pure {@link planPrune}. */
export interface PrunePlanInput {
  /** AT-URI of the active deployment (`site.current.uri`). Never pruned (§16). */
  currentDeploymentUri: string;
  /** Every deployment of the site the planner may consider, INCLUDING the current one. */
  deployments: DeploymentView[];
  /** AT-URIs of the deployments the publisher wants to prune. */
  targets: string[];
}

/** The computed prune plan: what to delete, what that releases, and what was refused. */
export interface PrunePlan {
  /** Deployments that will be deleted (the current version is never here). */
  pruned: DeploymentView[];
  /**
   * The delete writes for one atomic `applyWrites`: each pruned deployment's `app.szzt.deployment`
   * record followed by its `ing.dasl.masl` manifest record.
   */
  writes: ApplyWrite[];
  /** Blob CIDs no RETAINED manifest references after the prune — releasable by PDS GC (§16). */
  releasableBlobs: string[];
  /** Targets refused because they name the active version (`site.current`) — never pruned (§16). */
  refusedActive: string[];
  /** Targets that matched no supplied deployment. */
  unknown: string[];
}

/**
 * Compute which deployment/manifest records to delete and which blobs that releases (§16). Pure over
 * the supplied graph. Invariant, and the reason to keep this the single source of the decision: the
 * active version's deployment, manifest, and blobs are NEVER in the delete set or the releasable set
 * — the current deployment is always retained, so its blob CIDs stay referenced and cannot release.
 */
export function planPrune(input: PrunePlanInput): PrunePlan {
  const { currentDeploymentUri, deployments, targets } = input;
  const byUri = new Map(deployments.map((d) => [d.deploymentUri, d]));

  const pruned: DeploymentView[] = [];
  const refusedActive: string[] = [];
  const unknown: string[] = [];
  const prunedUris = new Set<string>();

  for (const target of targets) {
    if (target === currentDeploymentUri) {
      refusedActive.push(target);
      continue;
    }
    const view = byUri.get(target);
    if (view === undefined) {
      unknown.push(target);
      continue;
    }
    if (prunedUris.has(target)) continue; // ignore duplicate targets
    prunedUris.add(target);
    pruned.push(view);
  }

  // A blob is releasable iff it is referenced by a pruned manifest AND by no retained manifest.
  const retainedBlobs = new Set<string>();
  for (const d of deployments) {
    if (!prunedUris.has(d.deploymentUri)) {
      for (const cid of d.blobCids) retainedBlobs.add(cid);
    }
  }
  const releasable = new Set<string>();
  for (const d of pruned) {
    for (const cid of d.blobCids) {
      if (!retainedBlobs.has(cid)) releasable.add(cid);
    }
  }

  const writes: ApplyWrite[] = [];
  for (const d of pruned) {
    const dep = parseAtUri(d.deploymentUri);
    const man = parseAtUri(d.manifestUri);
    writes.push({ action: "delete", collection: dep.collection, rkey: dep.rkey });
    writes.push({ action: "delete", collection: man.collection, rkey: man.rkey });
  }

  return {
    pruned,
    writes,
    releasableBlobs: [...releasable],
    refusedActive,
    unknown,
  };
}

/** Thrown when execution finds a plan would delete the deployment `site.current` now names (§16). */
export class PruneActiveError extends Error {
  constructor(readonly activeDeploymentUri: string) {
    super(`refusing to prune the active version (site.current): ${activeDeploymentUri}`);
    this.name = "PruneActiveError";
  }
}

/** Options for {@link prune}. */
export interface PruneInput {
  client: PdsClient;
  /** rkey of the `app.szzt.site` record, re-read at execute time to re-check the active version. */
  siteRkey: string;
  /** A plan from {@link planPrune}. */
  plan: PrunePlan;
  /** Max `swapCommit` retries on concurrent-write conflict; defaults to 3. */
  maxRetries?: number;
}

/** Result of a successful {@link prune}. */
export interface PruneResult {
  /** AT-URIs of the deleted `app.szzt.deployment` records. */
  deletedDeployments: string[];
  /** AT-URIs of the deleted `ing.dasl.masl` manifest records. */
  deletedManifests: string[];
  /** Blob CIDs now referenced by no retained manifest — releasable by PDS GC (§16). */
  releasableBlobs: string[];
  /** New repo head after the delete commit, when the PDS returns it. */
  commit?: CommitRef;
}

/**
 * Execute a prune {@link PrunePlan}: delete the planned deployment + manifest records in ONE atomic,
 * `swapCommit`-guarded `applyWrites` (§7.1 write discipline reused). Before the write it re-reads
 * `site.current` and refuses (`PruneActiveError`, ZERO writes) if it now names a targeted deployment
 * — a concurrent rollback (§8) could have repointed `current` onto a version this plan wants to
 * delete, and §16's "active version MUST NOT be pruned" must hold at execution, not just plan time.
 * The `swapCommit` guard closes the race: a rollback landing after the read but before the write
 * fails the swap and forces a re-read + re-check on retry.
 */
export async function prune(input: PruneInput): Promise<PruneResult> {
  const { client, siteRkey, plan, maxRetries = 3 } = input;

  const empty: PruneResult = {
    deletedDeployments: [],
    deletedManifests: [],
    releasableBlobs: [],
  };
  if (plan.writes.length === 0) return empty;

  const targetDeploymentUris = new Set(plan.pruned.map((d) => d.deploymentUri));

  for (let attempt = 0; ; attempt++) {
    const head = await client.getLatestCommit();

    const site = await client.getRecord(COLLECTION.site, siteRkey);
    if (site === null) {
      throw new Error(`cannot prune: no ${COLLECTION.site} record at rkey ${siteRkey}`);
    }
    const current = readStrongRef(site.value, "current", COLLECTION.site);
    if (targetDeploymentUris.has(current.uri)) {
      throw new PruneActiveError(current.uri);
    }

    try {
      const res = await client.applyWrites({ writes: plan.writes, swapCommit: head.cid });
      return {
        deletedDeployments: plan.pruned.map((d) => d.deploymentUri),
        deletedManifests: plan.pruned.map((d) => d.manifestUri),
        releasableBlobs: plan.releasableBlobs,
        ...(res.commit !== undefined ? { commit: res.commit } : {}),
      };
    } catch (err) {
      if (err instanceof SwapFailedError && attempt < maxRetries) continue;
      throw err;
    }
  }
}
